import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { assetUrl } from "../lib/api";

export function RatingStars({ rating, size = 4, reviewTotal }) {
  return (
    <span className="inline-flex items-center gap-1" data-testid="rating-stars">
      <span className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-${size} h-${size} ${i <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
            style={{ width: size * 4, height: size * 4 }}
            aria-hidden="true"
          />
        ))}
      </span>
      <span className="text-sm font-semibold text-slate-700">{rating?.toFixed(1)}</span>
      {reviewTotal ? <span className="text-xs text-slate-400 font-medium">({reviewTotal.toLocaleString()})</span> : null}
    </span>
  );
}

// Multi-source ratings breakdown (real, linked). kind: saas | saas10 | app | consumer
export function RatingSources({ ratings, className = "" }) {
  if (!ratings || !ratings.length) return null;
  const label = (k) =>
    k === "app" ? " · mobile app" : k === "consumer" ? " · consumer reviews" : "";
  return (
    <div className={`space-y-2.5 ${className}`} data-testid="rating-sources">
      {ratings.map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-3 text-sm">
          <span className="text-slate-600">
            {r.url ? (
              <a href={r.url} target="_blank" rel="nofollow noopener noreferrer" className="text-sky-600 hover:underline font-medium">
                {r.source}
              </a>
            ) : (
              <span className="font-medium">{r.source}</span>
            )}
            <span className="text-xs text-slate-400">{label(r.kind)}</span>
          </span>
          <span className="font-semibold text-slate-800 shrink-0 tabular-nums">
            {r.kind === "saas10" ? `${r.score}/10` : `${Number(r.score).toFixed(1)}/5`}
            {r.count ? <span className="text-slate-400 font-normal"> · {r.count.toLocaleString()}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function SoftwareLogo({ software, size = "w-12 h-12" }) {
  if (software.logo_url) {
    return <img src={assetUrl(software.logo_url)} alt={`${software.name} logo`} className={`${size} rounded-xl object-contain bg-white border border-border p-1`} />;
  }
  return (
    <span className={`${size} rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center text-white font-heading font-bold text-lg`}>
      {software.name?.slice(0, 2).toUpperCase()}
    </span>
  );
}

export function SoftwareCard({ software, rank }) {
  return (
    <Link
      to={`/software/${software.slug}`}
      data-testid={`software-card-${software.slug}`}
      className="group block bg-white rounded-2xl border border-border p-6 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200"
    >
      <div className="flex items-start gap-4">
        {rank && (
          <span className="shrink-0 w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-sm">
            {rank}
          </span>
        )}
        <SoftwareLogo software={software} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-sky-600 transition-colors duration-200">{software.name}</h3>
            <RatingStars rating={software.rating} reviewTotal={software.review_total} />
          </div>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{software.tagline}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {(software.features || []).slice(0, 3).map((f) => (
              <span key={f} className="text-xs px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-medium">{f}</span>
            ))}
            <span className="text-xs font-semibold text-slate-700 ml-auto">{software.pricing_start}</span>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 shrink-0 mt-1 transition-colors duration-200" aria-hidden="true" />
      </div>
    </Link>
  );
}
